import {
  BioEthMethods,
  IBatch,
  Method,
  NonDisabledReadyStates,
  WineMethods,
  YeastMethods,
} from '@/features/single-measurement/types';
import i18n from '@/locales/i18n';

const getTurbidity = (batch: IBatch) => batch.turbidity;
const getViability = (batch: IBatch) => batch.resultStat.viab ?? 0;
const getTemperature = (batch: IBatch) => batch.temperature ?? 0;
const getSugarContent = (batch: IBatch) => batch.density ?? 0;
const getTotalCellCount = (batch: IBatch) => batch.resultStat.conz ?? 0;
const getViableCellCount = (batch: IBatch) => {
  const viab = getViability(batch);
  const conz = getTotalCellCount(batch);
  return conz * (viab / 100);
};
const getBuddingIndex = (batch: IBatch) => batch.resultStat.bud ?? 0;

const datapoints = [
  {
    yAxisID: 'y',
    borderColor: '#008A00',
    backgroundColor: '#008A00',
    extractor: getTotalCellCount,
    label: i18n.t('fermentations.graph.totalCellCount'),
    availableMethods: [...WineMethods, ...YeastMethods, ...BioEthMethods],
  },
  {
    yAxisID: 'y',
    borderColor: '#C73C9D',
    backgroundColor: '#C73C9D',
    extractor: getViableCellCount,
    label: i18n.t('fermentations.graph.viableCellCount'),
    availableMethods: [...WineMethods, ...YeastMethods, ...BioEthMethods],
  },
  {
    yAxisID: 'y1',
    hidden: true, // by default excluded from the graph
    borderColor: '#480052',
    extractor: getViability,
    backgroundColor: '#480052',
    label: i18n.t('fermentations.graph.viability'),
    availableMethods: [...WineMethods, ...YeastMethods, ...BioEthMethods],
  },
  {
    yAxisID: 'y1',
    borderColor: '#DAA520',
    hidden: true, // by default excluded from the graph
    extractor: getBuddingIndex,
    backgroundColor: '#DAA520',
    label: i18n.t('fermentations.graph.budding'),
    availableMethods: [...WineMethods, ...YeastMethods, ...BioEthMethods],
  },
  {
    yAxisID: 'y1',
    borderColor: '#000000',
    backgroundColor: '#000000',
    extractor: getSugarContent,
    label: i18n.t('fermentations.graph.sugarContent'),
    availableMethods: [...WineMethods],
  },
  {
    yAxisID: 'y1',
    borderColor: '#0A4C65',
    extractor: getTemperature,
    backgroundColor: '#0A4C65',
    label: i18n.t('fermentations.graph.temperature'),
    availableMethods: [...WineMethods],
  },
  {
    yAxisID: 'y1',
    borderColor: '#808080',
    extractor: getTurbidity,
    backgroundColor: '#808080',
    label: i18n.t('fermentations.graph.turbidity'),
    availableMethods: [...WineMethods],
  },
];

export const fermentationGraphDataMapper = ({
  method,
  batches,
  fermentationName = '',
  graphBoundary = 10,
  enableGraphBoundary = false,
  isReferenceCurve = false,
}: {
  method?: Method;
  batches?: IBatch[];
  fermentationName?: string;
  graphBoundary?: number;
  enableGraphBoundary?: boolean;
  isReferenceCurve?: boolean;
}) => {
  const datasets: any = [];

  const readyBatches = batches?.filter((batch) =>
    NonDisabledReadyStates.includes(batch.state),
  );

  // Go through all the datapoints and map data into its own lists
  for (const {
    extractor,
    availableMethods,
    hidden: defaultHidden,
    borderColor,
    backgroundColor,
    label,
    ...rest
  } of datapoints) {
    // If data point is applicable to the given method, data will be pushed to the array
    if (method && availableMethods.includes(method)) {
      const data = readyBatches?.map((batch) => ({
        x: Number(batch.locallyCreatedAt),
        y: extractor(batch),
      }));

      // Check if all y-values are 0
      const allYValuesZero = data?.every((point) => point.y === 0) ?? false;
      const hidden = defaultHidden || allYValuesZero;

      const graphCfg = { ...rest, borderColor, backgroundColor, hidden, label };

      const datasetCfg = {
        data,
        isReferenceCurve,
        tooltip: {
          callbacks: {
            label: (context: any) => extractLabel(context, fermentationName),
          },
        },
      };

      // Main dataset
      if (!isReferenceCurve) {
        datasets.push({ ...graphCfg, ...datasetCfg, showLine: true });
      }

      // Reference curve dataset
      if (isReferenceCurve) {
        // Show line only if boundary is not enabled
        const showLine = !enableGraphBoundary;
        datasets.push({
          ...graphCfg,
          ...datasetCfg,
          showLine,
          borderWidth: 2,
          borderDash: [2, 5],
          label: `${label} - ${fermentationName}`,
        });
      }

      // Only add corridor lines if there are actual data points and not all values are zero
      if (enableGraphBoundary && data && data.length > 0 && !allYValuesZero) {
        const boundaryCfg = {
          ...graphCfg,
          showLine: true,
          isBoundary: true,
          pointRadius: 0,
          pointStyle: false,
          borderDash: [2, 5],
          pointHoverRadius: 0,
        };

        // Add upper corridor
        const upperData = data.map((point) => ({
          x: point.x,
          y: point.y * (1 + graphBoundary / 100),
        }));
        datasets.push({
          ...boundaryCfg,
          data: upperData,
          label: label + ' - upper boundary',
        });

        // Add lower corridor
        const lowerData = data.map((point) => ({
          x: point.x,
          y: point.y * (1 - graphBoundary / 100),
        }));
        datasets.push({
          ...boundaryCfg,
          data: lowerData,
          label: label + ' - lower boundary',
        });
      }
    }
  }

  return { datasets };
};

const extractLabel = (context: any, fermentationName: string) => {
  const y = context.parsed.y;
  const value = Number.isInteger(y) ? y : y.toFixed(2);
  return `${value} ${context.dataset.label} - ${fermentationName}`;
};
